import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import SignupPage from '@/pages/Signup/SignupPage';
import SlugSignupPage from '@/pages/Signup/SlugSignupPage';
import InvitePage from '@/pages/Invite/InvitePage';
import LoginPage from '@/pages/Login/LoginPage';
import PublicCampaignPage from '@/creator/pages/PublicCampaign/PublicCampaignPage';
import FaqsPage from '@/creator/pages/Faqs/FaqsPage';
import HomePage from '@/creator/pages/Home/HomePage';
import CampaignsPage from '@/creator/pages/Campaigns/CampaignsPage';
import CampaignDetail from '@/creator/pages/Campaigns/CampaignDetail';
import WalletPage from '@/creator/pages/Wallet/WalletPage';
import ProfilePage from '@/creator/pages/Profile/ProfilePage';
import AppShell from '@/creator/AppShell';
import ImpersonationBanner from '@/creator/components/ImpersonationBanner';
import MarketingSiteRedirect from '@/creator/MarketingSiteRedirect';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import { handleBoundaryError } from '@/utility/reportRenderError';

/**
 * Feature routes land in their own PRs. Public signup is ROU-830; the logged-in
 * home/invitations/account and the personalised `/i/:uuid` link are ROU-838
 * (submission itself lives on the invite page, shared with guests); the public
 * `/c/:token` campaign page is ROU-872.
 *
 * `/signup` is the generic full wizard; pool-specific signups live under
 * `/signup/:slug`, whose section list, order, and data come from the backend
 * (ROU-923). `/login` is the standalone OTP sign-in (ROU-972).
 *
 * The creator-portal rework (ROU-1192) introduces the AppShell (home · campaigns
 * · wallet · account) around the logged-in area and a public LandingPage at `/`
 * for logged-out visitors. Signup and the invite/campaign links are unchanged.
 * That landing page has since moved to the marketing site at www.wave.me, so
 * logged-out visitors at `/` are redirected there (ROU-2032, marketing-site redirect).
 * The reworked Campaigns list (`/campaigns`) and its detail view
 * (`/campaigns/:campaignId`) fold in the old invitations/posts pages, which are
 * removed along with their routes.
 *
 * `/faqs` is public in both auth states (ROU-2006, wave FAQs page).
 *
 * The boundary wraps every route, not just the logged-in ones: AppShell's own
 * boundary covers the creator pages, but signup, login and the public
 * invite/campaign links render outside the shell, where an uncaught render
 * error would otherwise empty #root instead of showing a fallback (ROU-1718).
 */
function App() {
    const { isAuthenticated, isRestoring } = useWaveAuth();

    if (isRestoring) {
        return <div className="wm-root min-h-[100dvh] bg-wm-bg" aria-label="Loading Wave" />;
    }

    return (
        <div className="wm-root">
            <ImpersonationBanner />
            <ErrorBoundary
                onError={handleBoundaryError}
                fallbackRender={() => (
                    <p className="wm-sub p-6 text-center">Something went wrong. Please refresh the page.</p>
                )}
            >
                <Routes>
                    <Route path="/signup" element={isAuthenticated ? <Navigate to="/" replace /> : <SignupPage />} />
                    <Route path="/signup/:slug" element={<SlugSignupPage />} />
                    <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
                    <Route path="/i/:uuid" element={<InvitePage />} />
                    <Route path="/c/:token" element={<PublicCampaignPage />} />
                    <Route path="/faqs" element={<FaqsPage />} />
                    {isAuthenticated ? (
                        <>
                            <Route
                                path="/"
                                element={
                                    <AppShell>
                                        <HomePage />
                                    </AppShell>
                                }
                            />
                            <Route
                                path="/campaigns"
                                element={
                                    <AppShell>
                                        <CampaignsPage />
                                    </AppShell>
                                }
                            />
                            <Route
                                path="/campaigns/:campaignId"
                                element={
                                    <AppShell>
                                        <CampaignDetail />
                                    </AppShell>
                                }
                            />
                            <Route
                                path="/wallet"
                                element={
                                    <AppShell>
                                        <WalletPage />
                                    </AppShell>
                                }
                            />
                            <Route
                                path="/account"
                                element={
                                    <AppShell>
                                        <ProfilePage />
                                    </AppShell>
                                }
                            />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                    ) : (
                        <>
                            <Route path="/" element={<MarketingSiteRedirect />} />
                            <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                    )}
                </Routes>
            </ErrorBoundary>
        </div>
    );
}

export default App;
